import {
  AssessedControl,
  AssessmentControlId,
  AssessmentControlInstance,
  AssessmentId,
  ControlModuleSelection,
  PredefinedControlId,
  ProcessStepId,
  TprmScopeType,
  RecordControlCitation,
  SourceExtractId,
  CreateReviewAction,
  PendingBackgroundTask,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

export async function load(ids: AssessmentControlId[]) {
  return await platformedClient.internalRequest("post", "/region/{region}/api/tprm/assessed_controls/load_by_ids", {
    body: ids,
  });
}

export async function loadSourceExtracts(sourceExtractIds: SourceExtractId[]) {
  return await platformedClient.internalRequest("post", "/region/{region}/api/tprm/source_extracts/load", {
    body: sourceExtractIds,
  });
}

/**
 * Materialise the source extracts a control cites and return the resulting
 * citations. This writes no decision itself — the caller applies the returned
 * citations to the control decision through the normal decision-update path
 * (`bulk_decide`), same as any other decision change.
 */
export async function addEvidence(assessmentControlId: AssessmentControlId, citations: RecordControlCitation[]) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/controls/{assessment_control_id}/evidence",
    {path: {assessment_control_id: assessmentControlId}, body: citations},
  );
  return res;
}

export async function addCustomControl(
  assessmentId: AssessmentId,
  body: {
    title: string;
    description: string;
    modules: ControlModuleSelection;
    scope_types: TprmScopeType[];
    process_step_ids: ProcessStepId[];
  },
): Promise<AssessedControl> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/controls",
    {path: {assessment_id: assessmentId}, body},
  );
  await invalidateQueries([
    q.tprm.assessmentControlIds.filter(assessmentId),
    q.tprm.assessmentControlInstances.filter(assessmentId),
    q.tprm.assessedControl.filter(),
  ]);
  return res;
}

export async function removeControl(assessmentId: AssessmentId, assessmentControlId: AssessmentControlId) {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/assessments/{assessment_id}/controls/{assessment_control_id}",
    {path: {assessment_id: assessmentId, assessment_control_id: assessmentControlId}},
  );
  await invalidateQueries([
    q.tprm.assessmentControlIds.filter(assessmentId),
    q.tprm.assessmentControlInstances.filter(assessmentId),
    q.tprm.assessedControl.filter(),
    q.tprm.testLinkedControls.filter(),
  ]);
}

/** Control instances of an assessment, projected for the control assignment page. */
export async function listInstances(assessmentId: AssessmentId): Promise<AssessmentControlInstance[]> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/assessments/{assessment_id}/control_instances",
    {path: {assessment_id: assessmentId}},
  );
}

export async function addPredefinedControl(
  assessmentId: AssessmentId,
  predefinedControlId: PredefinedControlId,
  processStepIds: ProcessStepId[],
): Promise<AssessmentControlId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/predefined_controls",
    {
      path: {assessment_id: assessmentId},
      body: {predefined_control_id: predefinedControlId, process_step_ids: processStepIds},
    },
  );
  await invalidateQueries([
    q.tprm.assessmentControlInstances.filter(assessmentId),
    q.tprm.assessmentControlIds.filter(assessmentId),
    q.tprm.assessedControl.filter(),
  ]);
  return res;
}

export async function linkControlProcessStep(
  assessmentId: AssessmentId,
  assessmentControlId: AssessmentControlId,
  processStepId: ProcessStepId,
) {
  await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/assessed_controls/{assessment_control_id}/process_steps/{process_step_id}",
    {path: {assessment_control_id: assessmentControlId, process_step_id: processStepId}},
  );
  await invalidateQueries([q.tprm.assessmentControlInstances.filter(assessmentId), q.tprm.assessedControl.filter()]);
}

/** Leaves the control on the assessment; see `removeControl` to take it off entirely. */
export async function unlinkControlProcessStep(
  assessmentId: AssessmentId,
  assessmentControlId: AssessmentControlId,
  processStepId: ProcessStepId,
) {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/assessed_controls/{assessment_control_id}/process_steps/{process_step_id}",
    {path: {assessment_control_id: assessmentControlId, process_step_id: processStepId}},
  );
  await invalidateQueries([q.tprm.assessmentControlInstances.filter(assessmentId), q.tprm.assessedControl.filter()]);
}

/** Maps the framework's controls onto the understanding's process steps with AI,
 * replacing whatever a previous mapping run assigned. */
export async function mapStepControls(assessmentId: AssessmentId): Promise<PendingBackgroundTask> {
  const task: PendingBackgroundTask = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/map_step_controls",
    {path: {assessment_id: assessmentId}},
  );

  // Nothing subscribes to a task that isn't in the running-tasks list yet, so its
  // first progress event has nowhere to land.
  await invalidateQueries([q.tprm.engagementTasks.filter()]);

  return task;
}

export async function createReviews(assessmentControlId: AssessmentControlId, requests: CreateReviewAction[]) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/controls/{assessment_control_id}/reviews",
    {path: {assessment_control_id: assessmentControlId}, body: requests},
  );
  await invalidateQueries([q.tprm.assessedControl.filter(), q.reviewable.filter()]);
  return res;
}
